import { AnonymizationRules } from './types';

/**
 * Anonymizes sensitive information in a job offer text based on provided rules
 */
export function anonymizeOffer(
  text: string,
  companyName: string,
  rules: AnonymizationRules = {}
): string {
  if (!text) return '';

  let anonymizedText = text;

  // Replace company name with generic description
  if (companyName) {
    const companyRegex = new RegExp(companyName, 'gi');
    anonymizedText = anonymizedText.replace(
      companyRegex, 
      rules.companyDescription || 'une entreprise leader dans son secteur'
    );
  }

  // Complete list of first person to third person replacements
  const thirdPersonReplacements = [
    // Basic verb forms
    { 
      pattern: /\b(?:nous|notre équipe)\s+(?:sommes|avons|proposons|recherchons|offrons|souhaitons|développons|créons|concevons|accompagnons)\b/gi,
      replacement: 'notre client est'
    },
    // Possessive forms
    {
      pattern: /\bnos\s+(?:valeurs|équipes|bureaux|locaux|collaborateurs|clients|produits|services|solutions|projets|objectifs|ambitions)\b/gi,
      replacement: 'les $1 de notre client'
    },
    // Company references
    {
      pattern: /\bnotre\s+(?:entreprise|société|groupe|équipe|mission|vision|ambition|expertise|savoir-faire|métier|activité)\b/gi,
      replacement: 'notre client'
    },
    // Location references
    {
      pattern: /\bchez nous\b/gi,
      replacement: 'au sein de notre client'
    },
    {
      pattern: /\bavec nous\b/gi,
      replacement: 'avec notre client'
    },
    // Action calls
    {
      pattern: /\brejoignez-nous\b/gi,
      replacement: 'rejoignez notre client'
    },
    {
      pattern: /\bcontactez-nous\b/gi,
      replacement: 'contactez notre client'
    },
    // Complex verb forms
    {
      pattern: /\bnous\s+(?:vous\s+)?(?:permettons|garantissons|assurons|promettons)\b/gi,
      replacement: 'notre client permet'
    },
    // Future tense
    {
      pattern: /\bnous\s+(?:serons|ferons|développerons|accompagnerons)\b/gi,
      replacement: 'notre client sera'
    },
    // Present continuous
    {
      pattern: /\bnous\s+sommes\s+en\s+train\s+de\b/gi,
      replacement: 'notre client est en train de'
    },
    // Past tense
    {
      pattern: /\bnous\s+(?:avons|sommes)\s+(?:été|fait|créé|développé|mis)\b/gi,
      replacement: 'notre client a'
    },
    // Conditional
    {
      pattern: /\bnous\s+(?:pourrions|aimerions|souhaiterions)\b/gi,
      replacement: 'notre client pourrait'
    },
    // Benefits and advantages
    {
      pattern: /\bnous\s+(?:vous\s+)?offrons\b/gi,
      replacement: 'notre client offre'
    },
    // Team references
    {
      pattern: /\bnotre\s+(?:équipe|département|service)\b/gi,
      replacement: "l'équipe de notre client"
    },
    // Culture references
    {
      pattern: /\bnotre\s+culture\b/gi,
      replacement: 'la culture de notre client'
    }
  ];

  // Apply all third person replacements
  thirdPersonReplacements.forEach(({ pattern, replacement }) => {
    anonymizedText = anonymizedText.replace(pattern, replacement);
  });

  // Replace other sensitive information based on rules
  if (rules.replacements) {
    rules.replacements.forEach(({ pattern, replacement }) => {
      const regex = new RegExp(pattern, 'gi');
      anonymizedText = anonymizedText.replace(regex, replacement);
    });
  }

  // Replace email domains
  anonymizedText = anonymizedText.replace(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    'contact@entreprise.com'
  );

  // Replace phone numbers
  anonymizedText = anonymizedText.replace(
    /(?:\+33|0)\s*[1-9](?:[\s.-]*\d{2}){4}/g,
    '01 XX XX XX XX'
  );

  // Replace specific addresses while keeping city/region
  anonymizedText = anonymizedText.replace(
    /\d+[\s,]+(?:rue|avenue|boulevard|impasse|allée|place)[\s,]+[\w\s-]+(?=[\s,]+\d{5})/gi,
    'Adresse confidentielle'
  );

  // Replace specific brand names in hashtags
  anonymizedText = anonymizedText.replace(
    /#[A-Za-z0-9]+(?:Laure|Christine)/g,
    '#Retail'
  );

  return anonymizedText;
}

/**
 * Generates a generic company description based on sector and other attributes
 */
export function generateCompanyDescription(
  sector?: string,
  size?: string,
  market?: string
): string {
  const descriptions: Record<string, string> = {
    'tech': 'une entreprise innovante du secteur technologique',
    'retail': 'un acteur majeur du commerce de détail',
    'finance': 'un établissement financier de premier plan',
    'healthcare': 'un groupe leader dans le secteur de la santé',
    'manufacturing': 'un industriel de référence',
    'consulting': 'un cabinet de conseil reconnu',
    'default': 'une entreprise leader dans son secteur'
  };

  let description = descriptions[sector?.toLowerCase() || ''] || descriptions.default;

  if (size) {
    description += ` ${size}`;
  }

  if (market) {
    description += ` ${market}`;
  }

  return description;
}